import { DEPLOY_CHUNK_RECOVERY_STORAGE_PREFIX } from "@pinpoint/deploy-recovery";

export {
  DEPLOY_CHUNK_RECOVERY_OVERRIDE_KEY,
  DEPLOY_CHUNK_RECOVERY_STORAGE_PREFIX,
  currentEntryScriptSrc,
  installDeployChunkRecovery,
  lazyImportForRecovery,
  isStaleDeployChunkFailure,
  reloadForStaleDeployChunk,
  StaleDeployChunkError,
} from "@pinpoint/deploy-recovery";

const RECOVERY_PREFIX = DEPLOY_CHUNK_RECOVERY_STORAGE_PREFIX;

function snapshotRecoveryGuards(): Array<[string, string]> {
  const guards: Array<[string, string]> = [];
  try {
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (!key?.startsWith(RECOVERY_PREFIX)) continue;
      const value = sessionStorage.getItem(key);
      if (value !== null) guards.push([key, value]);
    }
  } catch {
    // Storage can be blocked in privacy-restricted browser contexts.
  }
  return guards;
}

function restoreRecoveryGuards(guards: Array<[string, string]>): void {
  try {
    guards.forEach(([key, value]) => sessionStorage.setItem(key, value));
  } catch {
    // A failed restoration cannot make the reset path throw or strand reload.
  }
}

// Product-only site-data wipe used by the dev toolbar and the error-boundary
// “Reset and reload” action. Automatic deploy recovery never calls this.
export async function clearSiteData(): Promise<void> {
  const recoveryGuards = snapshotRecoveryGuards();

  try {
    localStorage.clear();
  } catch {
    // Continue clearing other storage layers when one layer is unavailable.
  }

  try {
    sessionStorage.clear();
  } catch {
    // Continue clearing other storage layers when one layer is unavailable.
  }

  restoreRecoveryGuards(recoveryGuards);

  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // Cache Storage can reject in private browsing or restricted frames.
  }

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch {
    // Service-worker access can be denied by the current document context.
  }

  try {
    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.trim().split('=', 1)[0];
      if (!name) return;
      document.cookie = `${name}=;expires=${new Date().toUTCString()};path=/`;
    });
  } catch {
    // Cookie access is best effort and must not block the final reload.
  }
}
